<template>
    <v-menu
        v-model="menu"
        :close-on-content-click="false"
        :disabled="disabled || readonly"
        transition="scale-transition"
        offset-y
        :nudge-bottom="4"
        min-width="300"
        max-width="320"
    >
        <template v-slot:activator="{ on, attrs }">
            <v-text-field
                :value="displayValue"
                :label="label"
                :placeholder="placeholder"
                :outlined="outlined"
                :dense="dense"
                :disabled="disabled"
                :clearable="clearable"
                :hide-details="hideDetails"
                :rules="rules"
                :error-messages="errorMessages"
                :prepend-inner-icon="icon"
                :color="color"
                readonly
                v-bind="{ ...attrs, ...$attrs }"
                v-on="on"
                @click:clear="onClear"
            />
        </template>

        <div class="adp-panel" :class="{ 'adp-date': !isMonth }">
            <!-- Fast month + year navigation (date mode only) -->
            <div v-if="!isMonth" class="adp-nav">
                <v-select
                    v-model="selectedMonth"
                    :items="monthItems"
                    :color="color"
                    class="adp-select adp-month"
                    dense
                    outlined
                    hide-details
                    :menu-props="{ offsetY: true, maxHeight: 260 }"
                />
                <v-select
                    v-model="selectedYear"
                    :items="years"
                    :color="color"
                    class="adp-select adp-year"
                    dense
                    outlined
                    hide-details
                    :menu-props="{ offsetY: true, maxHeight: 260 }"
                />
            </div>

            <v-date-picker
                :value="value"
                :type="type"
                :min="min"
                :max="max"
                :allowed-dates="allowedDates"
                :color="color"
                :picker-date.sync="pickerDate"
                no-title
                @input="onInput"
                @change="onChange"
            />
        </div>
    </v-menu>
</template>

<script>
import moment from 'moment-timezone';

export default {
    name: 'AppDatePicker',
    inheritAttrs: false,
    props: {
        value: { default: null },
        label: { type: String, default: '' },
        placeholder: { type: String, default: '' },
        // 'date' (default) or 'month'
        type: { type: String, default: 'date' },
        min: { default: undefined },
        max: { default: undefined },
        allowedDates: { type: Function, default: undefined },
        color: { type: String, default: 'primary' },
        outlined: { type: Boolean, default: true },
        dense: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        readonly: { type: Boolean, default: false },
        clearable: { type: Boolean, default: false },
        hideDetails: { default: 'auto' },
        rules: { type: Array, default: () => [] },
        errorMessages: { default: undefined },
        icon: { type: String, default: 'mdi-calendar' },
        // moment format for the visible text (value stays ISO)
        displayFormat: { type: String, default: '' },
    },
    data() {
        return {
            menu: false,
            pickerDate: null, // 'YYYY-MM'
        };
    },
    computed: {
        isMonth() {
            return this.type === 'month';
        },
        valueFormat() {
            return this.isMonth ? 'YYYY-MM' : 'YYYY-MM-DD';
        },
        displayValue() {
            if (!this.value) return '';
            const fmt = this.displayFormat || (this.isMonth ? 'MMMM YYYY' : 'MMM D, YYYY');
            const m = moment(this.value, this.valueFormat, true);
            return m.isValid() ? m.format(fmt) : this.value;
        },
        years() {
            const currentYear = moment().year();
            const minYear = this.min ? moment(this.min).year() : currentYear - 100;
            const maxYear = this.max ? moment(this.max).year() : currentYear + 5;
            const list = [];
            for (let y = maxYear; y >= minYear; y--) list.push(y);
            return list;
        },
        monthItems() {
            return moment.months().map((name, i) => ({
                text: name,
                value: ('0' + (i + 1)).slice(-2),
            }));
        },
        selectedYear: {
            get() {
                return this.pickerDate ? Number(this.pickerDate.split('-')[0]) : moment().year();
            },
            set(year) {
                const mm = this.pickerDate ? this.pickerDate.split('-')[1] : moment().format('MM');
                this.pickerDate = `${year}-${mm}`;
            },
        },
        selectedMonth: {
            get() {
                return this.pickerDate ? this.pickerDate.split('-')[1] : moment().format('MM');
            },
            set(mm) {
                const year = this.pickerDate ? this.pickerDate.split('-')[0] : moment().year();
                this.pickerDate = `${year}-${mm}`;
            },
        },
    },
    watch: {
        menu(open) {
            if (open) this.syncPickerDate();
        },
        value() {
            this.syncPickerDate();
        },
    },
    methods: {
        syncPickerDate() {
            let base;
            if (this.value) base = moment(this.value, this.valueFormat, true);
            else if (this.max) base = moment(this.max);
            else if (this.min) base = moment(this.min);
            else base = moment();
            if (!base.isValid()) base = moment();
            this.pickerDate = base.format('YYYY-MM');
        },
        onInput(val) {
            this.$emit('input', val);
            // Close after a day is picked (month mode closes on month select too)
            this.menu = false;
        },
        onChange(val) {
            this.$emit('change', val);
        },
        onClear() {
            this.$emit('input', null);
            this.$emit('change', null);
        },
    },
    created() {
        this.syncPickerDate();
    },
};
</script>

<style lang="scss" scoped>
.adp-panel {
    background: #fff;
    border-radius: 10px;
    overflow: hidden;
}

.adp-nav {
    display: flex;
    gap: 8px;
    padding: 12px 12px 4px;
}

.adp-month {
    flex: 1 1 58%;
}

.adp-year {
    flex: 1 1 42%;
}

/* Let the menu container own the elevation, not the picker */
.adp-panel ::v-deep .v-picker {
    box-shadow: none;
    border-radius: 0;
}

/* Dropdowns replace the built-in month header in date mode */
.adp-date ::v-deep .v-date-picker-header {
    display: none;
}

.adp-panel ::v-deep .v-picker__body {
    margin: 0 auto;
}
</style>
